import { useRef, useLayoutEffect } from "react";
import { Link } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Text, Img, Heading } from "components";
import { SectionBadge } from "pages/AIHumanity/index";

gsap.registerPlugin(ScrollTrigger);

const linkChipStyle = {
    fontFamily: "Manrope, sans-serif",
    fontSize: "0.8rem",
    fontWeight: 600,
    color: "#6ddcff",
    textDecoration: "none",
    whiteSpace: "nowrap",
};

function IconPanel({ icon, label }) {
    return (
        <div
            className="product-img-anima flex-shrink-0 w-[38%] md:w-full sm:w-full flex items-center justify-center"
            style={{
                aspectRatio: "1 / 1",
                borderRadius: 20,
                border: "1px solid rgba(255,255,255,0.08)",
                background: "radial-gradient(circle at 30% 20%, rgba(109,220,255,0.14), transparent 60%), linear-gradient(160deg, #16161b, #0a0a0d)",
            }}
        >
            <div className="flex flex-col items-center gap-3">
                <span style={{ fontSize: 56, lineHeight: 1 }}>{icon}</span>
                {label && (
                    <span style={{
                        fontFamily: "Manrope, sans-serif",
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.35)",
                    }}>
                        {label}
                    </span>
                )}
            </div>
        </div>
    );
}

function UseCaseCard({ id, imgSrc, icon, iconLabel, title, subtitle, description, stat, links = [], flipped }) {
    return (
        <div
            id={id}
            className={`flex gap-14 items-start sm:flex-col sm:gap-8 ${flipped ? "flex-row-reverse" : "flex-row"} md:flex-col md:gap-8`}
        >
            {imgSrc ? (
                <Img
                    src={imgSrc}
                    className="product-img-anima flex-shrink-0 w-[38%] md:w-full sm:w-full"
                    alt={title}
                />
            ) : (
                <IconPanel icon={icon} label={iconLabel} />
            )}
            <div className="flex flex-col gap-3 flex-1 pt-2">
                <Heading size="xl" as="h3" className="!text-gray-300" style={{ lineHeight: "1.15" }}>
                    {title}
                </Heading>
                {subtitle && (
                    <Heading size="md" as="h4" style={{ color: "rgba(255,255,255,0.38)", fontWeight: 400 }}>
                        {subtitle}
                    </Heading>
                )}
                <Text size="s" as="p" className="mt-1" style={{ maxWidth: 420 }}>
                    {description}
                </Text>

                {stat && (
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 6 }}>
                        <span style={{
                            fontFamily: "Manrope, sans-serif",
                            fontSize: "1.1rem",
                            fontWeight: 700,
                            background: "linear-gradient(90deg, #6ddcff, #7e60f8)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                        }}>
                            {stat.val}
                        </span>
                        <span style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.8rem", color: "rgba(255,255,255,0.4)" }}>
                            {stat.label}
                        </span>
                    </div>
                )}

                {links.length > 0 && (
                    <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginTop: 10 }}>
                        {links.map((l) => (
                            l.external ? (
                                <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer" style={linkChipStyle}>
                                    {l.label} →
                                </a>
                            ) : (
                                <Link key={l.href} to={l.href} style={linkChipStyle}>
                                    {l.label} →
                                </Link>
                            )
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function ProductDescription() {
    const containerRef = useRef(null);

    useLayoutEffect(() => {
        const ctx = gsap.context(() => {
            const imgs = gsap.utils.toArray(".product-img-anima");
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: containerRef.current,
                    start: "top center",
                    end: "bottom bottom",
                    pin: false,
                    scrub: true,
                },
            });
            imgs.forEach((img) => {
                tl.fromTo(img, { y: 60, opacity: 0 }, { y: 0, opacity: 1, duration: 5, stagger: 0.15 });
            });
            tl.fromTo(
                ".product-final-section",
                { xPercent: 8, opacity: 0 },
                { xPercent: 0, opacity: 1, duration: 5, ease: "power2.out" }
            );
            tl.to({}, {}, "+=0.5");
        }, containerRef);

        return () => ctx.revert();
    }, []);

    return (
        /* Tailwind-only padding so md:/sm: overrides work (inline style beats class specificity) */
        <div
            className="bg-black-900_02 px-12 py-20 md:px-8 md:py-14 sm:px-5 sm:py-10"
            ref={containerRef}
        >
            <div className="flex flex-col gap-16 mx-auto md:gap-12 sm:gap-10" style={{ maxWidth: 1040 }}>

                <SectionBadge>Industries</SectionBadge>

                <div className="flex flex-col gap-16 md:gap-12 sm:gap-10">
                    <UseCaseCard
                        id="gaming"
                        imgSrc="images/Left_Face.png"
                        title="Gaming & Virtual Worlds"
                        subtitle="Character Relationship Layer"
                        description="Character voice, dialogue, and history that adapts to each player — driving immersion, retention, and virality."
                        stat={{ val: "Persistent", label: "character memory across sessions" }}
                        links={[
                            { label: "VoicePlay Engine", href: "https://voiceplay.aihumanity.io", external: true },
                            { label: "Player retention use case", href: "/use-cases/player-retention" },
                        ]}
                    />
                    <div className="h-px bg-gray-800" />

                    <UseCaseCard
                        id="companions"
                        imgSrc="images/Left_machine.png"
                        title="Companions & Assistants"
                        subtitle="Persistent Interaction Context"
                        description="Emotional continuity that turns daily usage into genuine trust — lifting DAU, renewal rates, and felt-understood scores."
                        stat={{ val: "24/7", label: "available, remembers every conversation" }}
                        links={[
                            { label: "AI Companion", href: "/products/companion" },
                            { label: "Reduce loneliness use case", href: "/use-cases/companionship" },
                        ]}
                        flipped
                    />
                    <div className="h-px bg-gray-800" />

                    <UseCaseCard
                        id="robotics"
                        imgSrc="images/Left_future.png"
                        title="Humanoid Robotics"
                        subtitle="Relational Intelligence Layer"
                        description="Voice, expression, and environment state fused into a relationship engine — making robots feel natural, usable, and worth the premium."
                        stat={{ val: "~50ms", label: "perception model latency" }}
                        links={[
                            { label: "AI Perception API", href: "/perception" },
                            { label: "Domain-tuned models use case", href: "/use-cases/domain-tuned-models" },
                        ]}
                    />
                    <div className="h-px bg-gray-800" />

                    <UseCaseCard
                        id="support-cx"
                        icon="📊"
                        iconLabel="Session Insights"
                        title="Customer Support & Research"
                        subtitle="Session Emotion Analytics"
                        description="Every support call, user interview, or usability test — turned into a structured report of how the person actually felt, without a QA lead re-listening to every recording."
                        stat={{ val: "Post-session", label: "structured report, not a raw recording" }}
                        links={[
                            { label: "Session Insights", href: "/products/session-insights" },
                            { label: "Turn every interaction into insight", href: "/use-cases/interaction-insights" },
                        ]}
                        flipped
                    />
                    <div className="h-px bg-gray-800" />

                    <UseCaseCard
                        id="hospitality-stays"
                        icon="🏠"
                        iconLabel="AI Front Desk"
                        title="Hospitality & Rental Stays"
                        subtitle="Voice + WeChat Front Desk Layer"
                        description="A WeChat-first AI front desk for homestays and serviced apartments, covering post-booking guest support from check-in through checkout with real-time voice interaction, issue triage, and service-quality feedback."
                        stat={{ val: "24/7", label: "arrival and in-stay guest coverage" }}
                        links={[
                            { label: "AI front desk use case", href: "/use-cases/ai-front-desk-for-homestays" },
                            { label: "Voice front desk demo", href: "/demo/voice-host" },
                        ]}
                    />
                </div>

                {/* Closing quote card */}
                <div
                    className="product-final-section rounded-2xl px-12 py-12 md:px-8 md:py-8 sm:px-5 sm:py-7 flex flex-col gap-5"
                    style={{
                        border: "1px solid rgba(255,255,255,0.07)",
                        background: "linear-gradient(20deg, #18181c, #0d0d10)",
                    }}
                >
                    <div className="flex items-center gap-2">
                        <Img src="images/img_television.svg" alt="logo" className="h-[20px]" />
                        <Img src="images/img_aihumanity.png" alt="AIHumanity" width="76" height="13" />
                    </div>
                    <Heading size="lg" as="blockquote" className="!text-gray-300" style={{ fontStyle: "italic", maxWidth: 780 }}>
                        "The next differentiator in AI is not intelligence. It's relationship quality.
                        AIHumanity is building that layer — starting in high-frequency virtual interaction,
                        expanding to every relational AI product."
                    </Heading>
                    <Text size="sm" style={{ color: "rgba(255,255,255,0.3)", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                        — David Chiu, Founder
                    </Text>
                    <Link
                        to="/demo"
                        style={{
                            fontFamily: "Manrope, sans-serif",
                            fontSize: "0.95rem",
                            fontWeight: 600,
                            padding: "0.75rem 2rem",
                            borderRadius: 12,
                            textDecoration: "none",
                            display: "inline-block",
                            alignSelf: "flex-start",
                            background: "linear-gradient(135deg, #6ddcff, #7e60f8)",
                            color: "#fff",
                        }}
                    >
                        See it in action →
                    </Link>
                </div>

            </div>
        </div>
    );
}
